import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Search, X } from 'lucide-react';
import { TICKETS } from '@/constants/testIds';
import { MultiSelectChips } from '@/components/shared/MultiSelectChips';

const toOpts = (arr) => (arr || []).map((x) => ({ value: x, label: x }));
const FILTER_KEYS = ['q', 'source', 'clinic_name', 'clinic_id', 'assignee_email', 'audiologist_id', 'city', 'from', 'to', 'month', 'year'];

export default function TicketsFiltersBar({ filters, setFilters }) {
  const [opts, setOpts] = useState({ sources: [], clinics: [], cities: [], audiologists: [] });
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    Promise.all([
      api.get('/reports/filters'),
      api.get('/reports/by-audiologist', { params: { limit: 300 } }).catch(() => ({ data: { data: [] } })),
    ]).then(([f, a]) => setOpts({
      sources: f.data.sources || [], clinics: f.data.clinics || [], cities: f.data.cities || [],
      audiologists: (a.data.data || []).map((x) => x.audiologist).filter(Boolean),
    })).catch(() => {});
  }, []);

  const active = FILTER_KEYS.some((k) => filters[k]);
  const clearAll = () => setFilters((f) => ({ ...f, ...Object.fromEntries(FILTER_KEYS.map((k) => [k, ''])) }));

  return (
    <div className="bg-stone-50 border-b border-stone-200 px-4 py-2 flex items-center gap-2 flex-wrap" data-testid={TICKETS.filtersBar}>
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
        <Input
          placeholder="Search patient name / ID / mobile…"
          value={filters.q}
          onChange={(e) => set('q', e.target.value)}
          className="h-8 pl-8 w-56 border-stone-300 bg-white"
          data-testid={TICKETS.searchInput}
        />
      </div>
      <MultiSelectChips label="Sources" value={filters.source} options={toOpts(opts.sources)}
        onChange={(v) => set('source', v)} testId={TICKETS.filterSource} width="w-36" />
      <MultiSelectChips label="Clinics" value={filters.clinic_name} options={toOpts(opts.clinics)}
        onChange={(v) => setFilters((f) => ({ ...f, clinic_name: v, clinic_id: '' }))} testId={TICKETS.filterClinic} width="w-40" />
      <MultiSelectChips label="Audiologists" value={filters.assignee_email} options={toOpts(opts.audiologists)}
        onChange={(v) => setFilters((f) => ({ ...f, assignee_email: v, audiologist_id: '' }))} testId={TICKETS.filterAudiologist} width="w-40" />
      <MultiSelectChips label="Cities" value={filters.city} options={toOpts(opts.cities)}
        onChange={(v) => set('city', v)} testId="tickets-filter-city" width="w-32" />
      <div className="flex items-center gap-1 text-[11px] text-stone-500">
        <span>Lead date</span>
        <Input type="date" value={filters.from} onChange={(e) => set('from', e.target.value)}
          className="h-8 w-36 text-xs bg-white border-stone-300" data-testid="tickets-filter-from" />
        <span>–</span>
        <Input type="date" value={filters.to} onChange={(e) => set('to', e.target.value)}
          className="h-8 w-36 text-xs bg-white border-stone-300" data-testid="tickets-filter-to" />
      </div>
      {active && (
        <button type="button" onClick={clearAll} className="h-8 px-2 text-xs text-rose-700 hover:bg-rose-50 rounded-md inline-flex items-center gap-1" data-testid="tickets-filters-clear">
          <X className="w-3 h-3" /> Clear filters
        </button>
      )}
    </div>
  );
}
